import { SlideUpdate } from 'api/slides';
import { CaseUpdate } from 'api/study';
import { isEqual, isNil } from 'es-toolkit/compat';
import { Procedure } from 'interfaces/procedure';
import { ProceduresFieldsContext } from 'interfaces/procedure/fields/helpers';
import { Slide } from 'interfaces/slide';
import { compact, filter, find, flatMap, forEach, get, isEmpty, map } from 'lodash';

import type { ManifestCaseTarget, ManifestDialogUrlState, ManifestDraftCase, ManifestDraftIssue } from './dialogState';
import { applyManifestDraftPatches, UploadedDuplicateSlideRow } from './manifestResolution';

export interface DuplicateRowOption {
  id: string;
  caseLabel: string;
  origin: string;
  // Only the fields that differ from the other rows of the same slide, already formatted for display.
  changes: string[];
}

export interface DuplicateSlideGroup {
  id: string;
  slideName: string;
  isIdentical: boolean;
  canMerge: boolean;
  options: DuplicateRowOption[];
}

type DuplicateRowEntry = {
  id: string;
  caseLabel: string;
  origin: string;
  caseFields: Partial<Procedure>;
  slide: Partial<Slide>;
  caseTarget?: ManifestCaseTarget;
  draftIssues?: ManifestDraftIssue[];
  // Only uploaded rows carry it: a row marked New case targets a new case even when its label matches a saved case.
  explicitNewCase?: boolean;
};

const CASE_FIELD_LABELS: Record<string, string> = {
  label: 'Case label',
  cancerTypeId: 'Cancer Type',
  cancerSubtypes: 'Cancer Subtype',
};
const SLIDE_FIELD_LABELS: Record<string, string> = {
  stainingType: 'Stain Type',
  biopsySiteId: 'Biopsy Site',
  biopsyType: 'Biopsy Type',
  preservationMethod: 'Preservation Method',
  procedureType: 'Procedure Type',
  negativeControl: 'Negative control',
  positiveControl: 'Positive control',
  metastasis: 'Metastasis',
  targetCaseAssignment: 'New case',
};

const normalize = (value: unknown) => (isNil(value) || value === '' ? null : value);

const getEntryValue = (entry: DuplicateRowEntry, source: 'caseFields' | 'slide', field: string) =>
  entry.draftIssues?.find((issue) => issue.field === field && issue.rawValue !== undefined)?.rawValue ??
  get(entry[source], field);

const formatValue = (field: string, value: unknown, context: ProceduresFieldsContext) => {
  const normalized = normalize(value);
  if (normalized === null || (Array.isArray(normalized) && isEmpty(normalized))) return 'Not set';
  if (typeof normalized === 'boolean') return normalized ? 'Yes' : 'No';
  if (Array.isArray(normalized)) return normalized.join(', ');
  if (field === 'cancerTypeId')
    return get(context, ['allCancerTypes', String(normalized), 'displayName'], String(normalized));
  if (field === 'biopsySiteId') {
    return find(context?.biopsySiteTypes, { id: Number(normalized) })?.displayName ?? String(normalized);
  }
  if (field === 'stainingType') {
    return find(context?.stainTypesNotDeprecated, { id: String(normalized) })?.displayName ?? String(normalized);
  }
  return String(normalized);
};

const buildGroup = (
  id: string,
  slideName: string,
  entries: DuplicateRowEntry[],
  context: ProceduresFieldsContext
): DuplicateSlideGroup => {
  const differingFields = (labels: Record<string, string>, source: 'caseFields' | 'slide') =>
    filter(Object.keys(labels), (field) => {
      const values = map(entries, (entry) => normalize(getEntryValue(entry, source, field)));
      return values.some((value) => !isEqual(value, values[0]));
    });
  const differingCaseFields = differingFields(CASE_FIELD_LABELS, 'caseFields');
  const differingSlideFields = differingFields(SLIDE_FIELD_LABELS, 'slide');
  const differsInNewCase = entries.some(
    (entry) => Boolean(entry.explicitNewCase) !== Boolean(entries[0].explicitNewCase)
  );
  const differsInCaseTarget = entries.some((entry) => entry.caseTarget !== entries[0].caseTarget);
  const sameCase = new Set(map(entries, 'caseLabel')).size === 1;
  const hasConflictingValue = (labels: Record<string, string>, source: 'caseFields' | 'slide') =>
    Object.keys(labels).some((field) => {
      const values = map(entries, (entry) => normalize(getEntryValue(entry, source, field))).filter(
        (value) => value !== null
      );
      return values.some((value) => !isEqual(value, values[0]));
    });
  return {
    id,
    slideName,
    isIdentical:
      isEmpty(differingCaseFields) && isEmpty(differingSlideFields) && !differsInNewCase && !differsInCaseTarget,
    canMerge:
      sameCase &&
      !differsInNewCase &&
      !differsInCaseTarget &&
      !hasConflictingValue(CASE_FIELD_LABELS, 'caseFields') &&
      !hasConflictingValue(SLIDE_FIELD_LABELS, 'slide'),
    options: map(entries, (entry) => ({
      id: entry.id,
      caseLabel: entry.caseLabel,
      origin: entry.origin,
      changes: [
        ...map(
          filter(differingCaseFields, (field) => field !== 'label'),
          (field) =>
            `${CASE_FIELD_LABELS[field]}: ${formatValue(field, getEntryValue(entry, 'caseFields', field), context)}`
        ),
        ...map(
          differingSlideFields,
          (field) =>
            `${SLIDE_FIELD_LABELS[field]}: ${formatValue(field, getEntryValue(entry, 'slide', field), context)}`
        ),
        ...(differsInNewCase
          ? [`New case: ${formatValue('explicitNewCase', Boolean(entry.explicitNewCase), context)}`]
          : []),
        ...(differsInCaseTarget
          ? [
              `Target case: ${
                entry.caseTarget === 'new'
                  ? 'New case'
                  : typeof entry.caseTarget === 'number'
                  ? `Existing case (ID ${entry.caseTarget})`
                  : 'Not selected'
              }`,
            ]
          : []),
      ],
    })),
  };
};

const groupEntries = (
  entriesBySlideId: Map<string, { slideName: string; entries: DuplicateRowEntry[] }>,
  context: ProceduresFieldsContext
) =>
  compact(
    map([...entriesBySlideId.entries()], ([slideKey, { slideName, entries }]) =>
      entries.length > 1 ? buildGroup(slideKey, slideName, entries, context) : null
    )
  );

// Option ids name a row by its case and by how many earlier rows of the same slide that case already holds.
const draftOptionId = (caseId: number, occurrence: number) => `${caseId}#${occurrence}`;

export const buildDraftDuplicateGroups = ({
  cases,
  casePatchesById,
  slidePatchesById,
  procedureFieldContext,
  targetsBySlideId = {},
}: {
  cases: ManifestDraftCase[];
  casePatchesById: Record<number, Partial<CaseUpdate>>;
  slidePatchesById: Record<string, Partial<SlideUpdate>>;
  procedureFieldContext: ProceduresFieldsContext;
  targetsBySlideId?: Readonly<Record<string, ManifestCaseTarget>>;
}) => {
  const entriesBySlideId = new Map<string, { slideName: string; entries: DuplicateRowEntry[] }>();
  forEach(applyManifestDraftPatches(cases, casePatchesById, slidePatchesById), (procedure) => {
    const occurrences = new Map<string, number>();
    forEach(procedure.slides, (slide) => {
      const occurrence = occurrences.get(slide.id) ?? 0;
      occurrences.set(slide.id, occurrence + 1);
      const group = entriesBySlideId.get(slide.id) ?? {
        slideName: slide.originalFileName || slide.id,
        entries: [],
      };
      group.entries.push({
        id: draftOptionId(procedure.id, occurrence),
        caseLabel: procedure.label || 'No case label',
        origin: 'Case assignment',
        caseFields: procedure,
        slide,
        draftIssues: procedure.draftIssues,
        caseTarget: procedure.draftTarget === undefined ? targetsBySlideId[slide.id] : procedure.draftTarget,
      });
      entriesBySlideId.set(slide.id, group);
    });
  });
  return groupEntries(entriesBySlideId, procedureFieldContext);
};

export const buildUploadDuplicateGroups = (
  rows: UploadedDuplicateSlideRow[],
  procedureFieldContext: ProceduresFieldsContext
) => {
  const entriesBySlideKey = new Map<string, { slideName: string; entries: DuplicateRowEntry[] }>();
  forEach(rows, ({ slideKey, slideLabel, rowNumber, procedure, explicitNewCase }) => {
    const group = entriesBySlideKey.get(slideKey) ?? { slideName: slideLabel, entries: [] };
    group.entries.push({
      id: String(rowNumber),
      caseLabel: procedure.label || 'No case label',
      origin: `Row ${rowNumber} of the file`,
      caseFields: procedure,
      slide: procedure.slides?.[0] ?? {},
      explicitNewCase,
    });
    entriesBySlideKey.set(slideKey, group);
  });
  return groupEntries(entriesBySlideKey, procedureFieldContext);
};

export const getDiscardedUploadRowNumbers = (
  groups: DuplicateSlideGroup[],
  keptOptionIdByGroupId: Record<string, string>
) =>
  flatMap(groups, (group) =>
    map(
      filter(group.options, (option) => option.id !== keptOptionIdByGroupId[group.id]),
      (option) => Number(option.id)
    )
  );

// Removes every stored row of a chosen slide except the kept one; an unknown kept row falls back to the first stored row.
export const keepChosenDraftRows = (
  previousState: ManifestDialogUrlState,
  keptOptionIdBySlideId: Record<string, string>
): ManifestDialogUrlState => {
  const lists = [previousState.draftCases || []];
  const rowsBySlideId = new Map<string, string[]>();
  forEach(lists, (cases) =>
    forEach(cases, (procedure) => {
      const occurrences = new Map<string, number>();
      forEach(procedure.slides, (slide) => {
        const occurrence = occurrences.get(slide.id) ?? 0;
        occurrences.set(slide.id, occurrence + 1);
        rowsBySlideId.set(slide.id, [...(rowsBySlideId.get(slide.id) ?? []), draftOptionId(procedure.id, occurrence)]);
      });
    })
  );
  const keptRowBySlideId = new Map<string, string>();
  forEach(Object.keys(keptOptionIdBySlideId), (slideId) => {
    const rows = rowsBySlideId.get(slideId) ?? [];
    keptRowBySlideId.set(
      slideId,
      rows.includes(keptOptionIdBySlideId[slideId]) ? keptOptionIdBySlideId[slideId] : rows[0]
    );
  });

  const filterList = (cases: ManifestDraftCase[]) =>
    compact(
      map(cases, (procedure) => {
        const occurrences = new Map<string, number>();
        const slides = filter(procedure.slides, (slide) => {
          const occurrence = occurrences.get(slide.id) ?? 0;
          occurrences.set(slide.id, occurrence + 1);
          const keptRow = keptRowBySlideId.get(slide.id);
          return keptRow === undefined || keptRow === draftOptionId(procedure.id, occurrence);
        });
        return isEmpty(slides) ? null : { ...procedure, slides };
      })
    );
  const draftCases = filterList(lists[0]);
  const assignments = { ...previousState.existingCaseAssignmentsBySlideId };
  for (const procedure of draftCases) {
    for (const slide of procedure.slides || []) {
      if (keptRowBySlideId.has(slide.id) && procedure.draftTarget !== undefined) {
        assignments[slide.id] = procedure.draftTarget;
      }
    }
  }
  return {
    ...previousState,
    draftCases,
    existingCaseAssignmentsBySlideId: assignments,
  };
};
